import { formatMs } from '@/lib/utils/format'
import type { LatencyStats } from '@/types/domain'

/**
 * Latency at four points, drawn on one axis.
 *
 * A bar chart of avg/p50/p95/max would imply four independent categories. They are four
 * positions on the same scale, so they are drawn as marks along a single line — which also
 * makes the gap between p50 and p95 visible, and that gap is the interesting part.
 */
export function LatencyDistribution({
  stats,
  label,
  accent = 'primary',
}: {
  stats: LatencyStats
  label: string
  accent?: 'primary' | 'remote'
}) {
  const scale = Math.max(stats.max_ms, 1)
  const marks = [
    { key: 'p50', value: stats.p50_ms, title: 'median' },
    { key: 'avg', value: stats.avg_ms, title: 'mean' },
    { key: 'p95', value: stats.p95_ms, title: '95th percentile' },
    { key: 'max', value: stats.max_ms, title: 'slowest' },
  ]
  const barColor = accent === 'primary' ? 'bg-primary-dim' : 'bg-remote'
  const markColor = accent === 'primary' ? 'bg-primary-bright' : 'bg-remote'

  if (stats.count === 0) {
    return (
      <div className="px-4 py-6 text-sm text-ink-muted">
        No {label.toLowerCase()} recorded in this window.
      </div>
    )
  }

  return (
    <div className="px-4 py-4">
      <div className="flex items-baseline justify-between">
        <p className="text-sm text-ink-dim">{label}</p>
        <p className="mono text-ink-muted">{stats.count.toLocaleString('en-US')} calls</p>
      </div>

      <div className="relative mt-4 h-1.5 rounded-sm bg-raised">
        <div
          className={`h-full origin-left animate-grow rounded-sm ${barColor}`}
          style={{ width: `${Math.min(100, (stats.p95_ms / scale) * 100)}%` }}
        />
        {marks.map((mark) => (
          <span
            key={mark.key}
            title={`${mark.title}: ${formatMs(mark.value)}`}
            className={`absolute top-1/2 h-3.5 w-0.5 -translate-y-1/2 ${markColor}`}
            style={{ left: `${Math.min(99.5, (mark.value / scale) * 100)}%` }}
          />
        ))}
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {marks.map((mark) => (
          <div key={mark.key}>
            <dt className="text-micro text-ink-muted" title={mark.title}>
              {mark.key}
            </dt>
            <dd className="mono text-ink-dim">{formatMs(mark.value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
